import { useCallback, useEffect, useState } from 'react'
import { api, type SentryProjectRepoMapResponse, type SentryProjectRepoMappingRow } from '../api/client'

type Props = {
  getAuthToken: () => Promise<string>
  refreshTrigger: number
}

function emptyRow(): SentryProjectRepoMappingRow {
  return { project_slug: '', github_repo: '' }
}

export function SentryProjectRepoMap({ getAuthToken, refreshTrigger }: Props) {
  const [data, setData] = useState<SentryProjectRepoMapResponse | null>(null)
  const [rows, setRows] = useState<SentryProjectRepoMappingRow[]>([emptyRow()])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveOk, setSaveOk] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await getAuthToken()
      const resp = await api.getSentryProjectRepoMap(token)
      setData(resp)
      setRows(resp.mappings.length > 0 ? resp.mappings : [emptyRow()])
    } catch (err) {
      setError((err as Error).message)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [getAuthToken])

  useEffect(() => {
    void load()
  }, [load, refreshTrigger])

  function updateRow(index: number, patch: Partial<SentryProjectRepoMappingRow>) {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)))
    setSaveOk(false)
    setError(null)
  }

  function addRow() {
    setRows((prev) => [...prev, emptyRow()])
    setSaveOk(false)
  }

  function removeRow(index: number) {
    setRows((prev) => (prev.length <= 1 ? [emptyRow()] : prev.filter((_, i) => i !== index)))
    setSaveOk(false)
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    setSaveOk(false)
    try {
      const token = await getAuthToken()
      const mappings = rows.filter((row) => row.project_slug.trim() && row.github_repo.trim())
      const resp = await api.putSentryProjectRepoMap(token, { mappings })
      setData(resp)
      setRows(resp.mappings.length > 0 ? resp.mappings : [emptyRow()])
      setSaveOk(true)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const projectOptions = data?.sentry_projects ?? []
  const repoOptions = data?.github_repos ?? []

  return (
    <div className="sentry-webhook-panel" style={{ marginTop: '16px' }}>
      <h3 className="sentry-webhook-title">Map Sentry projects to GitHub repos</h3>
      <p className="sentry-webhook-intro">
        When a Sentry alert fires, SelfHeal uses this mapping to pick the GitHub repository for
        remediation (PRs, branch fixes). Link each Sentry <strong>project slug</strong> to a repo
        allowed by your GitHub App installation.
      </p>

      {loading && <p className="sentry-webhook-muted">Loading project mappings…</p>}
      {error && <p className="cred-error">{error}</p>}

      {data && !loading && (
        <>
          {!data.sentry_api_configured && (
            <p className="sentry-webhook-hint">
              Save <strong>Organization slug</strong> and <strong>Auth token</strong> above to load
              Sentry projects in the dropdown.
            </p>
          )}
          {!data.github_connected && (
            <p className="sentry-webhook-hint">
              Install the SelfHeal GitHub App and select repositories before saving mappings.
            </p>
          )}
          {data.github_connected && repoOptions.length === 0 && (
            <p className="sentry-webhook-hint">
              GitHub App is installed but no repositories are allowlisted yet — grant repo access in
              the GitHub App settings, then refresh.
            </p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
            {rows.map((row, index) => (
              <div
                key={`map-row-${index}`}
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'end' }}
              >
                <div className="cred-field">
                  <label className="cred-label">Sentry project</label>
                  {projectOptions.length > 0 ? (
                    <select
                      className="cred-input"
                      value={row.project_slug}
                      onChange={(e) => updateRow(index, { project_slug: e.target.value })}
                    >
                      <option value="">Select project…</option>
                      {projectOptions.map((p) => (
                        <option key={p.slug} value={p.slug}>
                          {p.name} ({p.slug})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="cred-input"
                      value={row.project_slug}
                      onChange={(e) => updateRow(index, { project_slug: e.target.value })}
                      placeholder="e.g. node-fastify"
                    />
                  )}
                </div>
                <div className="cred-field">
                  <label className="cred-label">GitHub repository</label>
                  {repoOptions.length > 0 ? (
                    <select
                      className="cred-input"
                      value={row.github_repo}
                      onChange={(e) => updateRow(index, { github_repo: e.target.value })}
                    >
                      <option value="">Select repository…</option>
                      {repoOptions.map((repo) => (
                        <option key={repo} value={repo}>
                          {repo}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="cred-input"
                      value={row.github_repo}
                      onChange={(e) => updateRow(index, { github_repo: e.target.value })}
                      placeholder="owner/repo"
                    />
                  )}
                </div>
                <button
                  type="button"
                  className="sentry-webhook-copy-btn"
                  onClick={() => removeRow(index)}
                  aria-label="Remove mapping row"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
            <button type="button" className="sentry-webhook-copy-btn" onClick={addRow}>
              Add project
            </button>
            <button
              type="button"
              className="cred-save-btn"
              onClick={() => void handleSave()}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save project mappings'}
            </button>
          </div>

          {saveOk && (
            <p className="cred-success" style={{ marginTop: '8px' }}>
              Project mappings saved. New Sentry alerts will include the linked GitHub repository.
            </p>
          )}
        </>
      )}
    </div>
  )
}
